import { Injectable, inject } from '@angular/core';
import {
  Address,
  BillingInformation,
  CustomField,
  CustomFieldDetail,
  EndOrderDetails,
  EstablishmentCheckout,
  PaymentMethod,
  ShippingOption,
  TypesOfShippings,
  TransferPayer,
} from '@pedix-workspace/utils';
import { getPaymentMethodValue } from '@pedix-workspace/shared-models';
import { WebStorageService } from '@pedix-workspace/pedixapp-core-services';

export type EndOrderFormSession = {
  updated: Date;
  details: EndOrderForm;
};

export type EndOrderForm = {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  requestBilling?: boolean;
  billingInformation?: BillingInformation | null;
  shippingOption?: ShippingOption | null;
  paymentMethod?: PaymentMethod | null;
  tableNumber?: string | null;
  selectedSubsidiaryId?: string | null;
  howMuch?: number | null;
  address?: Address | null;
  customFieldDetailValues?: CustomFieldDetail['value'][] | null;
  couponCode?: string | null;
  transferPayer?: TransferPayer | null;

  // Food Bank Donation ------------------------------------------------
  bankFoodDonationChecked?: boolean;
};

@Injectable({ providedIn: 'root' })
export class EndOrderFormService {
  private readonly SESSION_STORAGE_KEY = 'PXW_ORDER_SESSION';
  private readonly ONE_HOUR_MS = 3600000; // 1 hora en milisegundos

  private webStorage = inject(WebStorageService);

  getInitialData(data: {
    storedEndOrderData: Pick<EndOrderDetails, 'personalInformation' | 'billingInformation'>;
    checkoutData: EstablishmentCheckout;
    selectedSubsidiaryId: string | null;
    previousState: EndOrderForm | null;
  }): EndOrderForm {
    if (data.previousState) {
      return {
        ...data.previousState,
        selectedSubsidiaryId: data.selectedSubsidiaryId,
        shippingOption: this.getMatchingShippingOption(
          data.previousState.shippingOption,
          data.checkoutData,
        ),
        paymentMethod: this.getMatchingPaymentMethod(
          data.previousState.paymentMethod,
          data.checkoutData,
        ),
      };
    }

    let address: Address = {};

    if (data.storedEndOrderData?.personalInformation?.address) {
      address = {
        ...data.storedEndOrderData.personalInformation.address,
        street: data.checkoutData.shippingOptionDelivery?.disableAddressInMap
          ? data.storedEndOrderData.personalInformation.address.street
          : null,
        addressDetails: data.checkoutData.shippingOptionDelivery?.disableAddressInMap
          ? null
          : data.storedEndOrderData.personalInformation.address.addressDetails,
      };
    }

    return {
      name: data.storedEndOrderData?.personalInformation?.name,
      phone: data.storedEndOrderData?.personalInformation?.phone,
      email: data.storedEndOrderData?.personalInformation?.email,
      requestBilling: false,
      billingInformation: data.storedEndOrderData?.billingInformation,
      transferPayer: data.storedEndOrderData?.personalInformation?.transferPayer ?? null,
      address,
      customFieldDetailValues: data.checkoutData.customFields?.map(customField =>
        this.getSanitizedCustomFieldDefaultValue(customField),
      ),
      selectedSubsidiaryId: data.selectedSubsidiaryId,
    };
  }

  getInitialDataFromOrder(order: EndOrderDetails) {
    return {
      name: order.personalInformation.name,
      phone: order.personalInformation.phone,
      email: order.personalInformation.email || order.parcelTrackingEmail || null,
      requestBilling: !!order.billingInformation,
      billingInformation: order.billingInformation,
      address: order.personalInformation.address,
      customFieldDetailValues: order.customFieldDetails?.map(
        customFieldDetail => customFieldDetail.value,
      ),
      couponCode: order.couponConfigurationSnapshot?.code,
      shippingOption: order.shippingOption,
      paymentMethod: order.paymentMethod,
      tableNumber: order.tableNumber,
      howMuch: order.howMuch,
    };
  }

  set sessionData(details: EndOrderForm) {
    // El TTL se maneja automáticamente por WebStorageService
    this.webStorage.setItem(this.SESSION_STORAGE_KEY, details, {
      storageType: 'session',
      ttl: this.ONE_HOUR_MS,
    });
  }

  get sessionData(): EndOrderForm | null {
    // WebStorageService maneja automáticamente la verificación de TTL
    const data = this.webStorage.getItem<EndOrderForm>(this.SESSION_STORAGE_KEY, {
      storageType: 'session',
    });

    // Si hay datos, refrescar el timer guardando de nuevo
    if (data) {
      this.sessionData = data;
    }

    return data;
  }

  clearSessionData() {
    this.webStorage.removeItem(this.SESSION_STORAGE_KEY, { storageType: 'session' });
  }

  private getMatchingShippingOption(
    shippingOption: ShippingOption | undefined | null,
    checkoutData: EstablishmentCheckout,
  ): ShippingOption | undefined | null {
    if (!shippingOption) {
      return null;
    }
    if (shippingOption.type === TypesOfShippings.DELIVERY) {
      return checkoutData.shippingOptionDelivery;
    }
    if (shippingOption.type === TypesOfShippings.TAKE_AWAY) {
      return checkoutData.shippingOptionTakeAway;
    }
    if (shippingOption.type === TypesOfShippings.IN_PLACE) {
      return checkoutData.shippingOptionInPlace;
    }
    if (shippingOption.type === TypesOfShippings.PARCEL) {
      return checkoutData.shippingOptionParcel;
    }
    return shippingOption;
  }

  private getMatchingPaymentMethod(
    paymentMethod: PaymentMethod | undefined | null,
    checkoutData: EstablishmentCheckout,
  ): PaymentMethod | undefined | null {
    if (!paymentMethod) {
      return null;
    }
    return checkoutData.paymentMethods.find(
      pm => getPaymentMethodValue(pm) === getPaymentMethodValue(paymentMethod),
    );
  }

  private getSanitizedCustomFieldDefaultValue(
    customField: CustomField,
  ): CustomFieldDetail['value'] | undefined {
    if (customField.defaultValue == null) {
      return undefined;
    }
    if (customField.fieldType === 'single-choice') {
      const isValidOption = customField.options?.some(
        option => option.value === customField.defaultValue,
      );
      return isValidOption ? customField.defaultValue : undefined;
    }
    if (customField.fieldType === 'multiple-choice' || customField.fieldType === 'date') {
      return undefined;
    }
    return customField.defaultValue;
  }
}
